/**
 * frontend/hooks/useAuth.js
 * Centralised auth state for SoundDAO.
 *
 *   1. On mount: getSession() → if session exists, fetch profile → loading=false
 *   2. onAuthStateChange SIGNED_IN: set loading=true, fetch profile, navigate to
 *      correct destination, loading=false
 *   3. onAuthStateChange SIGNED_OUT: clear state
 *   4. onAuthStateChange TOKEN_REFRESHED: update session token only
 *
 * Session identity is stable: Supabase hands us a NEW session object for the same user and
 * token several times per page load (getSession, INITIAL_SESSION, SIGNED_IN on restore/focus,
 * TOKEN_REFRESHED). Every setter goes through commitSession, which keeps the previous object
 * unless the access token or user actually changed, so consumers re-render — and effects keyed
 * on the session re-run — only for a real change.
 *
 * useAuth owns navigation on sign-in.  There is no AuthCallback component.
 * When Supabase fires SIGNED_IN (triggered by implicit-flow hash parsing on
 * /auth/callback), useAuth fetches the profile and navigates directly to
 * /dashboard/:persona or /onboarding.  App.js shows <LoadingScreen /> on the
 * /auth/callback path until this navigation fires.
 *
 * Exports: { session, user, loading, refreshUser, seedUser }
 *   session     — Supabase Auth session (null if logged out)
 *   user        — users table row (undefined=in-flight, null=no profile, object=loaded)
 *   loading     — true while initial check OR a post-SIGNED_IN fetch is in flight
 *   refreshUser — re-fetch profile from API (call after external mutations)
 *   seedUser    — inject a user object you already have (post-onboard response),
 *                 avoids a redundant GET /api/users/:id round-trip
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { supabase } from '../lib/supabaseClient';
import { noteSignedInUser, noteSignedOut } from '../lib/userStorage';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── Is this page load an actual sign-in arrival? ──────────────────────────────
// Supabase fires SIGNED_IN (and INITIAL_SESSION) not only when a magic link is
// exchanged but also when it RESTORES a stored session on a hard load, and again
// when the tab regains focus. Redirecting to the dashboard on every SIGNED_IN
// therefore bounced every signed-in deep link (/dig?q=goose, /playlist/<slug>,
// /creator/ivy …) to /dashboard/<persona> — the Prompt 5 live-check blocker.
// Only these count as "arriving from sign-in": the auth landing pages, or a URL
// that still carries auth tokens (implicit-flow hash, or PKCE/OTP query params).
export const AUTH_ARRIVAL_PATHS = ['/', '/login', '/auth/callback', '/onboarding'];
export function isAuthArrival(loc = window.location) {
  const { pathname = '/', hash = '', search = '' } = loc || {};
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (AUTH_ARRIVAL_PATHS.includes(path)) return true;
  if (/(^|[#&])(access_token|refresh_token)=/.test(hash)) return true;
  if (/[?&](code|token_hash)=/.test(search)) return true;
  return false;
}

/** Keep `prev` when `next` is the same user with the same access token (see header). */
export function stableSession(prev, next) {
  return (prev && next && prev.access_token === next.access_token && prev.user?.id === next.user?.id) ? prev : next;
}

export function useAuth() {
  const [session, setSession] = useState(null);
  // undefined = profile fetch not yet run / in-flight
  // null      = confirmed no profile (new user, 404 from API)
  // object    = loaded profile
  const [user,    setUser]    = useState(undefined);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // useNavigate() returns a new function on every location change; reading it
  // through a ref keeps the auth subscription from being torn down and re-made
  // (which re-emits INITIAL_SESSION) on every navigation.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const sessionUserRef    = useRef(null);   // user id of the session we already hold
  const arrivalHandledRef = useRef(null);   // access token whose arrival we already routed

  // ── fetchProfile ─────────────────────────────────────────────────────────────
  // Pulls the users table row for the authenticated user.
  // Returns the user object (or null) so callers can act on it immediately
  // without waiting for the setUser state update to commit.
  const fetchProfile = useCallback(async (authSession) => {
    if (!authSession?.user?.id) { setUser(null); return null; }
    try {
      // Sprint 6A.1: the owner view (email, wallet, referral code) needs the caller's token.
      // The token comes from the session we were handed — never supabase.auth.getSession()
      // inside an auth callback (it can deadlock).
      const res = await fetch(`${API_BASE}/api/users/${authSession.user.id}`, {
        headers: authSession.access_token ? { Authorization: `Bearer ${authSession.access_token}` } : {},
      });
      if (res.ok) {
        const { user: u } = await res.json();
        const profile = u ?? null;
        setUser(profile);
        // ── PostHog identify ──────────────────────────────────────────────
        if (profile) {
          try {
            posthog.identify(profile.id, {
              persona: profile.persona,
              rank:    profile.rank_title,
              city:    profile.city,
            });
          } catch (_) { /* analytics non-fatal */ }
        }
        return profile;
      } else if (res.status === 404) {
        setUser(null);
        return null;
      } else {
        // 5xx or other error: preserve a loaded profile; resolve undefined → null
        // so the app doesn't hang on LoadingScreen.
        setUser(prev => prev === undefined ? null : prev);
        return null;
      }
    } catch (err) {
      console.error('[useAuth] fetchProfile network error:', err.message,
        `\nURL: ${API_BASE}/api/users/${authSession.user.id}`);
      setUser(prev => prev === undefined ? null : prev);
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const commitSession = (s) => setSession(prev => stableSession(prev, s));

    // ── Initial session check ─────────────────────────────────────────────────
    // Handles the case where the user already has a valid session (returning
    // visit, page refresh).  Does NOT fire on magic link landing — that's
    // handled by the SIGNED_IN event below.
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!mounted) return;
      if (s && sessionUserRef.current === s.user?.id) { commitSession(s); setLoading(false); return; }
      sessionUserRef.current = s?.user?.id || null;
      // Sprint 6A.1: a different user than last time → drop the previous user's stored intents.
      if (s?.user?.id) noteSignedInUser(s.user.id);
      commitSession(s);
      if (s) await fetchProfile(s);
      if (mounted) setLoading(false);
    });

    // ── Auth state changes ────────────────────────────────────────────────────
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, s) => {
        if (!mounted) return;

        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          // INITIAL_SESSION with no session = signed out; getSession() above
          // already handles that (and the ordinary signed-in first render).
          if (!s) return;
          const arrival  = isAuthArrival();
          const sameUser = sessionUserRef.current === s.user?.id;
          sessionUserRef.current = s.user?.id || null;
          noteSignedInUser(s.user?.id);

          if (!arrival) {
            // A restored session, a focus refresh, or a sign-in in another tab:
            // keep the visitor exactly where they are. Only update the token, and
            // fetch the profile if this is a different (or first) user.
            commitSession(s);
            if (!sameUser) {
              setLoading(true);
              await fetchProfile(s);
              if (mounted) setLoading(false);
            }
            return;
          }

          // Real sign-in arrival (magic link landing on /auth/callback, or the
          // auth pages). SIGNED_IN and INITIAL_SESSION can both fire for the same
          // arrival; route it once per access token.
          if (arrivalHandledRef.current === s.access_token) { commitSession(s); return; }
          arrivalHandledRef.current = s.access_token;

          // useAuth owns navigation here — there is no AuthCallback listener
          // racing against this one.  We fetch the profile, then navigate
          // directly to the correct destination.  App.js shows <LoadingScreen />
          // on /auth/callback until this navigate() fires.
          //
          // setLoading(true) MUST come before setSession(s) so that authPending
          // is raised before any render triggered by the session change.
          setLoading(true);
          commitSession(s);
          const profile = await fetchProfile(s);
          if (mounted) {
            setLoading(false);
            if (profile?.persona) {
              navigateRef.current(`/dashboard/${profile.persona}`, { replace: true });
            } else {
              navigateRef.current('/onboarding', { replace: true });
            }
          }

        } else if (event === 'SIGNED_OUT') {
          try { posthog.reset(); } catch (_) { /* analytics non-fatal */ }
          noteSignedOut();   // Sprint 6A.1: no pending recruit/referral/claim survives a sign-out
          sessionUserRef.current = null;
          arrivalHandledRef.current = null;
          setSession(null);
          setUser(undefined);
          setLoading(false);

        } else if (event === 'TOKEN_REFRESHED') {
          // Session token rotated — profile unchanged, just update the token.
          commitSession(s);

        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  return {
    session,
    user,
    loading,
    refreshUser: useCallback(() => fetchProfile(session), [fetchProfile, session]),
    seedUser: setUser,
  };
}
